/**
 * Form doğrulama yardımcı işlevleri
 */

import { isValidPhoneNumber, isValidEmail as validateEmail, isValidTCKimlikNo, isValidIBAN, isValidBloodType } from './formatters';

// Re-export the functions from formatters
export const isValidEmail = validateEmail;
export { isValidPhoneNumber, isValidTCKimlikNo, isValidIBAN, isValidBloodType };

/**
 * Şifre gücünü kontrol et
 * @param {string} password - Kontrol edilecek şifre
 * @returns {Object} Şifre gücü bilgisi
 */
export const checkPasswordStrength = (password) => {
  if (!password) {
    return { score: 0, message: 'Şifre gerekli', color: 'red' };
  }
  
  let score = 0;
  
  // Uzunluk kontrolü
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Karakter çeşitliliği
  if (/[a-z]/.test(password)) score += 1; // Küçük harf
  if (/[A-Z]/.test(password)) score += 1; // Büyük harf
  if (/[0-9]/.test(password)) score += 1; // Rakam
  if (/[^a-zA-Z0-9]/.test(password)) score += 1; // Özel karakter
  
  let message, color;
  
  switch (true) {
    case (score < 3):
      message = 'Zayıf şifre';
      color = 'red';
      break;
    case (score < 5):
      message = 'Orta şifre';
      color = 'orange';
      break;
    default:
      message = 'Güçlü şifre';
      color = 'green';
      break;
  }
  
  return { score, message, color };
};

/**
 * İki şifrenin eşleşip eşleşmediğini kontrol et
 * @param {string} password - İlk şifre
 * @param {string} confirmPassword - Doğrulama şifresi
 * @returns {boolean} Şifreler eşleşiyor mu?
 */
export const doPasswordsMatch = (password, confirmPassword) => {
  return password === confirmPassword;
};

/**
 * Kullanıcı adının geçerli olup olmadığını kontrol et
 * @param {string} username - Kontrol edilecek kullanıcı adı
 * @returns {boolean} Kullanıcı adı geçerli mi?
 */
export const isValidUsername = (username) => {
  if (!username) return false;
  
  // Alfanümerik, alt çizgi ve nokta içerebilir, 3-20 karakter arasında olmalı
  const usernameRegex = /^[a-zA-Z0-9._]{3,20}$/;
  return usernameRegex.test(username);
};

/**
 * Ethereum cüzdan adresinin geçerli olup olmadığını kontrol et
 * @param {string} address - Kontrol edilecek Ethereum adresi
 * @returns {boolean} Adres geçerli mi?
 */
export const isValidEthereumAddress = (address) => {
  if (!address) return false;
  
  // Ethereum adres formatı kontrol (0x ile başlayan 42 karakter uzunluğunda hex)
  const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/;
  return ethAddressRegex.test(address);
};

/**
 * Bir formun geçerli olup olmadığını kontrol et
 * @param {Object} formData - Form verileri
 * @param {Array} requiredFields - Gerekli alan isimleri
 * @returns {Object} Geçerlilik durumu ve hata mesajları
 */
export const validateForm = (formData, requiredFields = []) => {
  const errors = {};
  let isValid = true;
  
  // Gerekli alanları kontrol et
  requiredFields.forEach(field => {
    if (!formData[field]) {
      errors[field] = 'Bu alan gereklidir';
      isValid = false;
    }
  });
  
  // E-posta doğrulama
  if (formData.email && !isValidEmail(formData.email)) {
    errors.email = 'Geçerli bir e-posta adresi giriniz';
    isValid = false;
  }
  
  // Şifre doğrulama
  if (formData.password) {
    const passwordStrength = checkPasswordStrength(formData.password);
    if (passwordStrength.score < 3) {
      errors.password = passwordStrength.message;
      isValid = false;
    }
    
    // Şifre eşleşme kontrolü
    if (formData.confirmPassword && !doPasswordsMatch(formData.password, formData.confirmPassword)) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
      isValid = false;
    }
  }
  
  // Kullanıcı adı doğrulama
  if (formData.username && !isValidUsername(formData.username)) {
    errors.username = 'Kullanıcı adı 3-20 karakter arasında olmalı ve sadece harf, rakam, nokta ve alt çizgi içerebilir';
    isValid = false;
  }
  
  // Telefon numarası doğrulama
  if (formData.phoneNumber && !isValidPhoneNumber(formData.phoneNumber)) {
    errors.phoneNumber = 'Geçerli bir telefon numarası giriniz';
    isValid = false;
  }
  
  return { isValid, errors };
};

/**
 * Input verilerini güvenli hale getir (XSS koruması)
 * @param {string} input - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // HTML karakterlerini escape et
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Profil verilerini doğrula
 * @param {Object} profileData - Doğrulanacak profil verisi
 * @returns {Object} Doğrulanmış profil verisi ve hatalar
 */
export const validateProfileData = (profileData) => {
  const errors = {};
  const sanitizedData = {};
  
  // Genel bilgiler validasyonu
  if (profileData.name) {
    const sanitizedName = sanitizeInput(profileData.name);
    if (sanitizedName.length < 2) {
      errors.name = 'İsim en az 2 karakter olmalıdır';
    } else if (sanitizedName.length > 50) {
      errors.name = 'İsim 50 karakterden uzun olamaz';
    } else {
      sanitizedData.name = sanitizedName;
    }
  }
  
  if (profileData.tcKimlikNo) {
    const sanitizedTC = sanitizeInput(profileData.tcKimlikNo);
    if (!isValidTCKimlikNo(sanitizedTC)) {
      errors.tcKimlikNo = 'Geçerli bir TC Kimlik numarası giriniz';
    } else {
      sanitizedData.tcKimlikNo = sanitizedTC;
    }
  }
  
  if (profileData.phone) {
    const sanitizedPhone = sanitizeInput(profileData.phone);
    if (!isValidPhoneNumber(sanitizedPhone)) {
      errors.phone = 'Geçerli bir telefon numarası giriniz';
    } else {
      sanitizedData.phone = sanitizedPhone;
    }
  }
  
  if (profileData.email) {
    const sanitizedEmail = sanitizeInput(profileData.email);
    if (!isValidEmail(sanitizedEmail)) {
      errors.email = 'Geçerli bir e-posta adresi giriniz';
    } else {
      sanitizedData.email = sanitizedEmail.toLowerCase();
    }
  }
  
  // Sağlık bilgileri validasyonu
  if (profileData.bloodType) {
    const sanitizedBloodType = sanitizeInput(profileData.bloodType);
    if (!isValidBloodType(sanitizedBloodType)) {
      errors.bloodType = 'Geçerli bir kan grubu seçiniz';
    } else {
      sanitizedData.bloodType = sanitizedBloodType.toUpperCase();
    }
  }
  
  if (profileData.height) {
    const height = parseInt(profileData.height);
    if (isNaN(height) || height < 50 || height > 250) {
      errors.height = 'Boy 50-250 cm arasında olmalıdır';
    } else {
      sanitizedData.height = height;
    }
  }
  
  if (profileData.weight) {
    const weight = parseFloat(profileData.weight);
    if (isNaN(weight) || weight < 20 || weight > 300) {
      errors.weight = 'Kilo 20-300 kg arasında olmalıdır';
    } else {
      sanitizedData.weight = weight;
    }
  }
  
  // Acil durum kişileri validasyonu
  if (profileData.emergencyContacts && Array.isArray(profileData.emergencyContacts)) {
    const validContacts = [];
    profileData.emergencyContacts.forEach((contact, index) => {
      const contactErrors = {};
      
      if (!contact.name || sanitizeInput(contact.name).length < 2) {
        contactErrors.name = 'İsim gereklidir';
      }
      
      if (!contact.phone || !isValidPhoneNumber(contact.phone)) {
        contactErrors.phone = 'Geçerli telefon numarası gereklidir';
      }
      
      if (!contact.relationship) {
        contactErrors.relationship = 'Yakınlık derecesi gereklidir';
      }
      
      if (Object.keys(contactErrors).length > 0) {
        errors[`contact_${index}`] = contactErrors;
      } else {
        validContacts.push({
          name: sanitizeInput(contact.name),
          phone: sanitizeInput(contact.phone),
          relationship: sanitizeInput(contact.relationship)
        });
      }
    });
    sanitizedData.emergencyContacts = validContacts;
  }
  
  // Adres validasyonu
  if (profileData.address && typeof profileData.address === 'object') {
    const addressErrors = {};
    const sanitizedAddress = {};
    
    if (profileData.address.street) {
      const street = sanitizeInput(profileData.address.street);
      if (street.length < 5) {
        addressErrors.street = 'Sokak/Cadde bilgisi en az 5 karakter olmalıdır';
      } else {
        sanitizedAddress.street = street;
      }
    }
    
    if (profileData.address.district) {
      sanitizedAddress.district = sanitizeInput(profileData.address.district);
    }
    
    if (profileData.address.city) {
      sanitizedAddress.city = sanitizeInput(profileData.address.city);
    }
    
    if (profileData.address.postalCode) {
      const postalCode = sanitizeInput(profileData.address.postalCode);
      if (!/^\d{5}$/.test(postalCode)) {
        addressErrors.postalCode = 'Posta kodu 5 haneli olmalıdır';
      } else {
        sanitizedAddress.postalCode = postalCode;
      }
    }
    
    if (Object.keys(addressErrors).length > 0) {
      errors.address = addressErrors;
    } else {
      sanitizedData.address = sanitizedAddress;
    }
  }
  
  // Tıbbi bilgiler validasyonu
  if (profileData.allergies && Array.isArray(profileData.allergies)) {
    sanitizedData.allergies = profileData.allergies
      .map(allergy => sanitizeInput(allergy))
      .filter(allergy => allergy.length > 0);
  }
  
  if (profileData.medications && Array.isArray(profileData.medications)) {
    sanitizedData.medications = profileData.medications
      .map(medication => sanitizeInput(medication))
      .filter(medication => medication.length > 0);
  }
  
  if (profileData.conditions && Array.isArray(profileData.conditions)) {
    sanitizedData.conditions = profileData.conditions
      .map(condition => sanitizeInput(condition))
      .filter(condition => condition.length > 0);
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    data: sanitizedData
  };
};

/**
 * Acil durum kişisi validasyonu
 * @param {object} contact - Acil durum kişisi
 * @param {number} index - Index numarası
 * @returns {object} Validasyon sonucu
 */
export const validateEmergencyContact = (contact, index = 0) => {
  const errors = {};
  
  if (!contact.name || sanitizeInput(contact.name).length < 2) {
    errors[`contact_${index}_name`] = 'İsim gereklidir';
  }
  
  if (!contact.phone || !isValidPhoneNumber(contact.phone)) {
    errors[`contact_${index}_phone`] = 'Geçerli telefon numarası gereklidir';
  }
  
  if (!contact.relationship) {
    errors[`contact_${index}_relationship`] = 'Yakınlık derecesi gereklidir';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};